import React, {useEffect, useState} from "react";
import {useNavigate, useParams} from "react-router-dom";
import Loader from "../../components/Loader";
import SuccessAlert from "../../components/SuccessAlert";
import ErrorAlert from "../../components/ErrorAlert";
import axiosInstance from "../../utils/axiosInstance";
import {IoArrowBackSharp} from "react-icons/io5";

const EditUser = () => {
    const {id} = useParams();
    const navigate = useNavigate();
    const [user, setUser] = useState();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [saving, setSaving] = useState(false);
    const [message, setMessage] = useState();

    useEffect(() => {
        const fetchUser = async () => {
            try {
                const response = await axiosInstance.get(`/users/${id}`);
                setUser(response.data);
            } finally {
                setLoading(false);
            }
        };

        fetchUser();
    }, [id]);

    const handleInputChange = (e) => {
        const {name, value} = e.target;
        setUser((prevUser) => ({
            ...prevUser,
            [name]: value,
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError("");
        setMessage("");
        setSaving(true);
        try {
            await axiosInstance.put(`/users/${id}`, user);
            setSaving(false);
            setMessage("User updated");
            navigate("/admin/all-users");
        } catch (error) {
            if (error.response.status === 400) {
                setError(error.response.data.message);
                return;
            }
            setError("Failed to update user.");
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <div>
                <Loader/>
            </div>
        );
    }

    return (
        <div className="mx-auto max-w-[1500px] space-y-5 px-4 pb-4 sm:px-6 sm:pb-6">
            <h3 className="mb-1 flex items-center gap-3 text-2xl font-bold tracking-tight text-gray-900 dark:text-white">
                {/* Go Back Button */}
                <button
                    onClick={() => window.history.back()} // This will navigate back to the previous page
                    className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-gray-300 text-gray-500 transition hover:border-red-400/60 hover:text-red-500 dark:border-[#3a3a3d] dark:text-slate-400"
                >
                    <IoArrowBackSharp size={24}/>
                </button>
                <span className="h-6 w-1.5 shrink-0 rounded-full bg-gradient-to-b from-red-500 to-orange-400" />
                Edit User
            </h3>

            <form
                onSubmit={handleSubmit}
                className="p-6 rounded-[22px] border border-gray-200 bg-white shadow-lg shadow-gray-900/5 dark:border-[#2c2c2e] dark:bg-[#1c1c1e] dark:shadow-black/40"
            >
                {message && (
                    <div>
                        <SuccessAlert message={message}/>
                        <br/>
                    </div>
                )}
                {error && (
                    <div>
                        <ErrorAlert errorMessage={error}/>
                        <br/>
                    </div>
                )}
                <div className="mb-4">
                    <label className="mb-1.5 block text-xs font-bold uppercase tracking-[0.08em] text-gray-500 dark:text-slate-400">
                        Username (Not Changeable)
                    </label>
                    <div
                        className="flex h-11 w-full items-center rounded-[12px] border border-gray-200 bg-gray-50 px-4 text-sm text-gray-600 dark:border-[#2c2c2e] dark:bg-[#161617] dark:text-slate-400">
                        {user.username}
                    </div>
                </div>

                <div className="mb-4">
                    <label className="mb-1.5 block text-xs font-bold uppercase tracking-[0.08em] text-gray-500 dark:text-slate-400">
                        Email (Not Changeable)
                    </label>
                    <div
                        className="flex h-11 w-full items-center rounded-[12px] border border-gray-200 bg-gray-50 px-4 text-sm text-gray-600 dark:border-[#2c2c2e] dark:bg-[#161617] dark:text-slate-400">
                        {user.email}
                    </div>
                </div>

                <div className="mb-4">
                    <label className="mb-1.5 block text-xs font-bold uppercase tracking-[0.08em] text-gray-500 dark:text-slate-400">Phone</label>
                    <input
                        type="text"
                        name="phone"
                        value={user.phone}
                        onChange={handleInputChange}
                        className="h-11 w-full rounded-[12px] border border-gray-300 bg-white px-4 text-sm text-gray-800 outline-none transition placeholder:text-gray-400 focus:border-red-400 focus:ring-4 focus:ring-red-500/10 disabled:opacity-60 dark:border-[#3a3a3d] dark:bg-[#262628] dark:text-slate-100 dark:placeholder:text-slate-500"
                    />
                </div>

                <div className="mb-4">
                    <label className="mb-1.5 block text-xs font-bold uppercase tracking-[0.08em] text-gray-500 dark:text-slate-400">Country</label>
                    <input
                        type="text"
                        name="country"
                        value={user.country}
                        onChange={handleInputChange}
                        className="h-11 w-full rounded-[12px] border border-gray-300 bg-white px-4 text-sm text-gray-800 outline-none transition placeholder:text-gray-400 focus:border-red-400 focus:ring-4 focus:ring-red-500/10 disabled:opacity-60 dark:border-[#3a3a3d] dark:bg-[#262628] dark:text-slate-100 dark:placeholder:text-slate-500"
                    />
                </div>

                <div className="mb-4">
                    <label className="mb-1.5 block text-xs font-bold uppercase tracking-[0.08em] text-gray-500 dark:text-slate-400">Credit Balance</label>
                    <input
                        type="number"
                        name="credit_balance"
                        value={user.credit_balance}
                        onChange={handleInputChange}
                        className="h-11 w-full rounded-[12px] border border-gray-300 bg-white px-4 text-sm text-gray-800 outline-none transition placeholder:text-gray-400 focus:border-red-400 focus:ring-4 focus:ring-red-500/10 disabled:opacity-60 dark:border-[#3a3a3d] dark:bg-[#262628] dark:text-slate-100 dark:placeholder:text-slate-500"
                    />
                </div>

                {/*<div className="mb-4">*/}
                {/*    <label className="mb-1.5 block text-xs font-bold uppercase tracking-[0.08em] text-gray-500 dark:text-slate-400">AI Credit Balance</label>*/}
                {/*    <input*/}
                {/*        type="number"*/}
                {/*        name="ai_credit_balance"*/}
                {/*        value={user.ai_credit_balance}*/}
                {/*        onChange={handleInputChange}*/}
                {/*        className="h-11 w-full rounded-[12px] border border-gray-300 bg-white px-4 text-sm text-gray-800 outline-none transition placeholder:text-gray-400 focus:border-red-400 focus:ring-4 focus:ring-red-500/10 disabled:opacity-60 dark:border-[#3a3a3d] dark:bg-[#262628] dark:text-slate-100 dark:placeholder:text-slate-500"*/}
                {/*    />*/}
                {/*</div>*/}

                <div className="mb-4">
                    <label className="mb-1.5 block text-xs font-bold uppercase tracking-[0.08em] text-gray-500 dark:text-slate-400">Role</label>
                    <select
                        name="role"
                        value={user.role}
                        onChange={handleInputChange}
                        className="h-11 w-full rounded-[12px] border border-gray-300 bg-white px-4 text-sm text-gray-800 outline-none transition placeholder:text-gray-400 focus:border-red-400 focus:ring-4 focus:ring-red-500/10 disabled:opacity-60 dark:border-[#3a3a3d] dark:bg-[#262628] dark:text-slate-100 dark:placeholder:text-slate-500"
                    >
                        <option value="CUSTOMER">Customer</option>
                        <option value="ADMIN">Admin</option>
                    </select>
                </div>

                <div className="mb-4">
                    <label className="mb-1.5 block text-xs font-bold uppercase tracking-[0.08em] text-gray-500 dark:text-slate-400">Status</label>
                    <select
                        name="status"
                        value={user.status}
                        onChange={handleInputChange}
                        className="h-11 w-full rounded-[12px] border border-gray-300 bg-white px-4 text-sm text-gray-800 outline-none transition placeholder:text-gray-400 focus:border-red-400 focus:ring-4 focus:ring-red-500/10 disabled:opacity-60 dark:border-[#3a3a3d] dark:bg-[#262628] dark:text-slate-100 dark:placeholder:text-slate-500"
                    >
                        <option value="ACTIVE">Active</option>
                        <option value="DEACTIVE">Deactive</option>
                    </select>
                </div>

                <button
                    type="submit"
                    className="inline-flex h-11 items-center justify-center gap-2 rounded-full bg-gradient-to-r from-red-600 to-orange-500 px-6 text-sm font-bold text-white shadow-lg shadow-red-500/25 transition hover:from-red-500 hover:to-orange-400 disabled:cursor-not-allowed disabled:opacity-60"
                    disabled={saving}
                >
                    {saving ? "Saving..." : "Save Changes"}
                </button>

                <button
                    type="button"
                    className="ml-4 inline-flex h-11 items-center justify-center gap-2 rounded-full border border-gray-300 bg-white px-5 text-sm font-semibold text-gray-700 transition hover:border-red-400/60 disabled:opacity-60 dark:border-[#3a3a3d] dark:bg-[#262628] dark:text-slate-200"
                    onClick={() => navigate.push(`/admin/users/${id}`)}
                >
                    Cancel
                </button>
            </form>
        </div>
    );
};

export default EditUser;
